import React, { useState, useEffect } from "react";
import axios from "axios";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Disable prop-types warnings for DataTable cell render props
/* eslint-disable react/prop-types */

// API Configuration
import API_BASE_URL from "config/api";

// API Passcode for admin operations
const API_PASSCODE = process.env.REACT_APP_API_PASSCODE || "bnova123";

function SSODomains() {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDomain, setEditingDomain] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    domain: "",
    allowedOrigins: "",
    verifyUrl: "",
    sharedSecret: "",
    description: "",
    isActive: true,
  });

  // Fetch SSO domains
  const fetchDomains = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/sso-domains?passcode=${API_PASSCODE}`);
      if (response.data.success) {
        setDomains(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching SSO domains:", error);
      showSnackbar("Failed to load SSO domains", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDomains();
  }, []);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const resetForm = () => {
    setFormData({
      name: "",
      domain: "",
      allowedOrigins: "",
      verifyUrl: "",
      sharedSecret: "",
      description: "",
      isActive: true,
    });
    setEditingDomain(null);
  };

  const handleOpenDialog = (domain = null) => {
    if (domain) {
      setEditingDomain(domain);
      setFormData({
        name: domain.name || "",
        domain: domain.domain || "",
        allowedOrigins: Array.isArray(domain.allowedOrigins)
          ? domain.allowedOrigins.join(", ")
          : domain.domain || "",
        verifyUrl: domain.verifyUrl || "",
        sharedSecret: "", // Don't show existing secret for security
        description: domain.description || "",
        isActive: domain.isActive !== undefined ? domain.isActive : true,
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    resetForm();
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.name || !formData.domain || !formData.verifyUrl || !formData.sharedSecret) {
      showSnackbar("Please fill in all required fields", "warning");
      return;
    }

    try {
      const payload = {
        name: formData.name,
        domain: formData.domain,
        allowedOrigins: formData.allowedOrigins
          .split(",")
          .map((origin) => origin.trim())
          .filter((origin) => origin),
        verifyUrl: formData.verifyUrl,
        sharedSecret: formData.sharedSecret,
        description: formData.description,
        isActive: formData.isActive,
      };

      let response;
      if (editingDomain) {
        response = await axios.put(
          `${API_BASE_URL}/sso-domains/${
            editingDomain._id || editingDomain.id
          }?passcode=${API_PASSCODE}`,
          payload
        );
      } else {
        response = await axios.post(
          `${API_BASE_URL}/sso-domains?passcode=${API_PASSCODE}`,
          payload
        );
      }

      if (response.data.success) {
        showSnackbar(
          editingDomain ? "SSO domain updated successfully" : "SSO domain created successfully",
          "success"
        );
        handleCloseDialog();
        fetchDomains();
      }
    } catch (error) {
      console.error("Error saving SSO domain:", error);
      showSnackbar(error.response?.data?.error || "Failed to save SSO domain", "error");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this SSO domain?")) {
      return;
    }

    try {
      const response = await axios.delete(
        `${API_BASE_URL}/sso-domains/${id}?passcode=${API_PASSCODE}`
      );
      if (response.data.success) {
        showSnackbar("SSO domain deleted successfully", "success");
        fetchDomains();
      }
    } catch (error) {
      console.error("Error deleting SSO domain:", error);
      showSnackbar("Failed to delete SSO domain", "error");
    }
  };

  const handleToggle = async (id, currentStatus) => {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/sso-domains/${id}/toggle?passcode=${API_PASSCODE}`
      );
      if (response.data.success) {
        showSnackbar(
          `Domain ${!currentStatus ? "activated" : "deactivated"} successfully`,
          "success"
        );
        fetchDomains();
      }
    } catch (error) {
      console.error("Error toggling domain:", error);
      showSnackbar("Failed to toggle domain status", "error");
    }
  };

  // Table columns
  const columns = [
    { Header: "Name", accessor: "name", width: "15%" },
    { Header: "Domain", accessor: "domain", width: "15%" },
    {
      Header: "Allowed Origins",
      accessor: "allowedOrigins",
      width: "20%",
      Cell: ({ value }) => {
        const origins = Array.isArray(value) ? value : [];

        const firstTwo = origins.length > 2 ? origins.slice(0, 2) : origins;

        return (
          <Box>
            {firstTwo.length > 0 ? (
              firstTwo.map((origin, idx) => (
                <Chip key={idx} label={origin} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
              ))
            ) : (
              <MDTypography variant="caption" color="text">
                {value || "N/A"}
              </MDTypography>
            )}
            {origins.length > 2 && (
              <MDTypography variant="caption" color="text">
                +{origins.length - 2} more
              </MDTypography>
            )}
          </Box>
        );
      },
    },
    {
      Header: "Status",
      accessor: "isActive",
      width: "10%",
      Cell: ({ value, row }) => (
        <Switch
          checked={value}
          onChange={() => handleToggle(row.original._id || row.original.id, value)}
          size="small"
        />
      ),
    },
    {
      Header: "Description",
      accessor: "description",
      width: "20%",
      Cell: ({ value }) => (
        <MDTypography variant="caption" color="text" noWrap>
          {value || "-"}
        </MDTypography>
      ),
    },
    {
      Header: "Actions",
      accessor: "actions",
      width: "20%",
      Cell: ({ row }) => (
        <MDBox display="flex" gap={1}>
          <Tooltip title="Edit">
            <IconButton size="small" color="info" onClick={() => handleOpenDialog(row.original)}>
              <Icon>edit</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(row.original._id || row.original.id)}
            >
              <Icon>delete</Icon>
            </IconButton>
          </Tooltip>
        </MDBox>
      ),
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Guidance card explaining how to onboard new SSO clients */}
        <MDBox mb={3}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" gutterBottom>
                How to connect a new app with SSO
              </MDTypography>
              <MDTypography variant="body2" color="text" gutterBottom>
                Each external app (like an LMS) must expose a secure verify endpoint and share a
                secret with BNOVA. When that app logs a user in, it calls BNOVA&apos;s SSO API with
                the user&apos;s access token and its domain. BNOVA looks up the matching SSO Domain
                below and calls the partner&apos;s verify endpoint to confirm the session.
              </MDTypography>
              <MDTypography variant="body2" color="text">
                <strong>Steps:</strong> 1) Partner implements a POST endpoint (e.g.
                <code> /verify-bnova-sso</code>) that validates the token and email and returns
                <code> &#123; valid: true, user: &#123; ... &#125; &#125;</code> on success. 2) You
                agree on a shared secret and configure it in both systems. 3) Add an entry below
                with the partner&apos;s domain, allowed origins, verify URL, and shared secret. 4)
                The partner sends
                <code> &#123; supabaseToken, userEmail, domain &#125;</code> to BNOVA&apos;s SSO API
                using the same domain you configure here.
              </MDTypography>
            </MDBox>
          </Card>
        </MDBox>

        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
                  <MDTypography variant="h5">SSO Domain Management</MDTypography>
                  <MDButton variant="gradient" color="info" onClick={() => handleOpenDialog()}>
                    <Icon sx={{ mr: 1 }}>add</Icon>
                    Add SSO Domain
                  </MDButton>
                </MDBox>
                <MDBox p={3}>
                  <DataTable
                    table={{ columns, rows: domains }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={true}
                    noEndBorder
                  />
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "grey.900",
            color: "white",
          },
        }}
      >
        <DialogTitle sx={{ color: "white" }}>
          {editingDomain ? "Edit SSO Domain" : "Add New SSO Domain"}
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <MDInput
                  fullWidth
                  label="Name *"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Kitab LMS"
                />
              </Grid>
              <Grid item xs={12}>
                <MDInput
                  fullWidth
                  label="Domain *"
                  name="domain"
                  value={formData.domain}
                  onChange={handleInputChange}
                  placeholder="e.g., kitab.ai"
                />
              </Grid>
              <Grid item xs={12}>
                <MDInput
                  fullWidth
                  label="Allowed Origins (comma-separated)"
                  name="allowedOrigins"
                  value={formData.allowedOrigins}
                  onChange={handleInputChange}
                  placeholder="e.g., https://kitab.ai, https://www.kitab.ai"
                  helperText="Leave empty to use domain as origin"
                />
              </Grid>
              <Grid item xs={12}>
                <MDInput
                  fullWidth
                  label="Verify URL *"
                  onChange={handleInputChange}
                  name="verifyUrl"
                  value={formData.verifyUrl}
                  placeholder="https://partner.example.com/verify-bnova-sso"
                />
              </Grid>
              <Grid item xs={12}>
                <MDInput
                  fullWidth
                  type="password"
                  label="Shared Secret *"
                  name="sharedSecret"
                  value={formData.sharedSecret}
                  onChange={handleInputChange}
                  placeholder="Enter shared secret for this SSO client"
                  helperText={editingDomain ? "Leave empty to keep existing secret" : ""}
                />
              </Grid>
              <Grid item xs={12}>
                <MDInput
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Optional description"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      name="isActive"
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseDialog} color="secondary">
            Cancel
          </MDButton>
          <MDButton onClick={handleSubmit} variant="gradient" color="info">
            {editingDomain ? "Update" : "Create"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Footer />
    </DashboardLayout>
  );
}

export default SSODomains;
